import { useState, useEffect, useCallback } from 'react';
import { fetchWeatherData } from '../services/weatherService';
import { getStoredData, setStoredData } from '../utils/localStorageService';
import { toast } from 'react-toastify';
import { ERROR_MESSAGES, CONFIG } from '../constants';

/**
 * Custom hook to manage weather data state
 * @returns {Object} - Weather state and functions
 */
export const useWeatherData = () => {
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [location, setLocation] = useState('');
  const [lastVisited, setLastVisited] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load last visited from localStorage on mount
  useEffect(() => {
    const savedLastVisited = getStoredData('lastVisited');
    if (savedLastVisited) {
      try {
        setLastVisited(JSON.parse(savedLastVisited));
      } catch (e) {
        console.error('Error parsing lastVisited:', e);
      }
    }

    const savedLocation = getStoredData('location');
    if (savedLocation) {
      setLocation(savedLocation);
    }
  }, []);

  // Fetch weather data when location changes
  useEffect(() => {
    const loadWeatherData = async () => {
      if (!location) return;

      setLoading(true);
      setError(null);

      try {
        const data = await fetchWeatherData(location);
        setWeather(data.current);
        setForecast(data.forecast);

        // Update last visited
        const newLastVisited = [
          location,
          ...lastVisited.filter(l => l !== location)
        ].slice(0, CONFIG.MAX_RECENT_SEARCHES);
        
        setLastVisited(newLastVisited);
        setStoredData('lastVisited', JSON.stringify(newLastVisited));
        setStoredData('location', location);
      } catch (error) {
        const errorMessage = error.message || ERROR_MESSAGES.WEATHER_FETCH_FAILED;
        setError(errorMessage);
        toast.error(errorMessage);
        console.error('Weather fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    loadWeatherData();
  }, [location]);

  const clearHistory = useCallback(() => {
    setLastVisited([]);
    localStorage.removeItem('lastVisited');
  }, []);

  const updateLocation = useCallback((newLocation) => {
    setLocation(newLocation);
  }, []);

  return {
    weather,
    forecast,
    location,
    lastVisited,
    loading,
    error,
    updateLocation,
    clearHistory,
  };
};

export default useWeatherData;
